import { useEffect, useState } from "react";

import { api } from "@/api/client";

/**
 * The running build, under the app name.
 *
 * It used to sit at the foot of the sidebar, below the account, in twelve-point
 * grey. It was on screen at every window size - measured - and it was still
 * reported twice as missing, which is the only verdict that counts. An
 * indicator nobody finds is not an indicator.
 *
 * So it moved to the top, under the name, where a version conventionally lives
 * and where the eye already goes. Only the number and the commit: the date is
 * in the tooltip and the whole lot is in Settings, and a header is the wrong
 * place to spend three facts where one will do.
 *
 * Asks the server rather than reading a compiled-in constant: after a deploy
 * the browser may still be running the previous bundle from cache, so a baked
 * version would confidently report the old one at the exact moment someone is
 * checking whether the new one landed.
 */
export default function VersionBadge() {
  const [build, setBuild] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .health()
      .then((info) => !cancelled && setBuild(info))
      .catch(() => {
        /* A version in the corner is never worth an error on screen. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!build) return null;

  const dated = build.commitDate
    ? new Date(build.commitDate).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div
      className="version-badge"
      title={
        `Version ${build.version}\nCommit ${build.commit}` +
        (dated ? `\nCommitted ${dated}` : "") +
        (build.dirty ? "\nBuilt with uncommitted changes" : "")
      }
    >
      <span className="version-number">v{build.version}</span>
      <span className="version-commit">
        {build.commit}
        {build.dirty ? "+" : ""}
      </span>
    </div>
  );
}
